/**
 * External Imports
 */
import {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo
} from 'react';
import { connect, useDispatch } from 'react-redux';
/**
 * Internal Imports
 */
import SearchHeader from '../../../components/searchHeader';
import EmployeeRoundsReportTable from '../../../components/customTables/reports/employee/roundsReportTable';
import { RootState } from '../../../redux/store';
import {
  clearEmployeeRoundsReport,
  clearRoundsReportFilter,
  getCSVToExport,
  getCSVToExportSuccess,
  getDataToDownloadSuccess
} from '../../../redux/actions';

import { date } from '../../../utils/helpers';
import {
  useGetEmployeeRoundsReportQuery,
  useLazyGetEmployeeRoundsReportDownloadQuery
} from '../../../redux/apis/Reports.api';

/**
 * Global Type Definition Imports
 */
import {
  AuthUserProps,
  EmpDepartment,
  EmpRoundsReport,
  Filter
} from '../../../config/interfaces';

/**
 * Style Imports
 */
import { ReportContainer } from '../styles/ReportsViews.styles';

interface RoundsReportProps {
  authorizedUser: AuthUserProps;
  allEmpDepartments: EmpDepartment[];
  currentEmployeeRoundsReportPage: number;
  reportSearchQuery: string;
  roundsReportFilter: Filter;
  searchTerm: string;
  selectedEmpDepartments: EmpDepartment[];
  sortKey: string;
  sortDir: string;
  setSearchTerm: Dispatch<SetStateAction<string>>;
}

const RoundsReport: FC<RoundsReportProps> = ({
  authorizedUser,
  allEmpDepartments,
  currentEmployeeRoundsReportPage,
  reportSearchQuery,
  roundsReportFilter,
  searchTerm,
  selectedEmpDepartments,
  sortKey,
  sortDir,
  setSearchTerm
}) => {
  const dispatch = useDispatch();

  const [
    triggerEmployeeRoundsExport,
    { isLoading: isEmployeeRoundsExportLoading }
  ] = useLazyGetEmployeeRoundsReportDownloadQuery();
  const {
    data: employeeRoundsReportData,
    // isError: isGetEmployeeRoundsReportError,
    isFetching: isGetEmployeeRoundsReportFetching,
    isLoading: isGetEmployeeRoundsReportLoading
  } = useGetEmployeeRoundsReportQuery(
    {
      facilityId: authorizedUser?.facilityId,
      query: reportSearchQuery || '',
      roundedBy: roundsReportFilter.employeeId || '',
      templateName: roundsReportFilter.template || '',
      sortBy: sortKey,
      sortOrder: sortDir,
      departments:
        selectedEmpDepartments.length > 0
          ? selectedEmpDepartments.length === allEmpDepartments.length
            ? 'ALL'
            : selectedEmpDepartments.map(dept => dept.departmentId).join(',')
          : '',
      startday: date.formatFilterStartDate(
        roundsReportFilter.startDate || date.firstDayOfMonth()
      ),
      endday: date.formatFilterEndDate(
        roundsReportFilter.endDate || date.todayDateWithDashes()
      ),
      page: currentEmployeeRoundsReportPage
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !(authorizedUser?.facilityId && selectedEmpDepartments)
    }
  );

  useEffect(() => {
    return () => {
      dispatch(clearRoundsReportFilter());
      dispatch(clearEmployeeRoundsReport());
    };
  }, []);

  useEffect(() => {
    if (isEmployeeRoundsExportLoading) {
      dispatch(getCSVToExport());
    }
  }, [isEmployeeRoundsExportLoading]);

  const listToDisplay = useMemo(() => {
    if (employeeRoundsReportData?.roundReportList.length) {
      const cleanData = employeeRoundsReportData.roundReportList.map(
        (round: EmpRoundsReport) => ({
          ...round,
          department: round?.employeeDept || '',
          employee:
            `${round?.employeeFirstName} ${round?.employeeLastName}` || '',
          roundedBy:
            round?.complianceStatus === 'notrounded'
              ? ''
              : `${round?.userFirstName} ${round?.userLastName}`,
          createdUserId: round?.userId || '',
          date: round?.roundDate || '',
          status:
            round?.complianceStatus === 'compliant'
              ? 'Compliant'
              : round?.complianceStatus === 'notrounded'
              ? 'Not Rounded'
              : 'Rounded',
          templateName:
            round?.complianceStatus === 'notrounded' ? '' : round?.templateName
        })
      );

      return cleanData;
    }
    return [];
  }, [employeeRoundsReportData, selectedEmpDepartments]);

  const exportParams = useMemo(
    () => ({
      facilityId: authorizedUser?.facilityId,
      query: reportSearchQuery || '',
      roundedBy: roundsReportFilter.employeeId || '',
      templateName: roundsReportFilter.template || '',
      departments:
        selectedEmpDepartments.length === allEmpDepartments.length
          ? 'ALL'
          : selectedEmpDepartments.map(dept => dept.departmentId).join(','),
      sortBy: sortKey,
      sortOrder: sortDir,
      startday: date.formatFilterStartDate(
        roundsReportFilter.startDate || date.firstDayOfMonth()
      ),
      endday: date.formatFilterEndDate(
        roundsReportFilter.endDate || date.todayDateWithDashes()
      )
    }),
    [
      authorizedUser?.facilityId,
      reportSearchQuery,
      roundsReportFilter,
      selectedEmpDepartments,
      sortKey,
      sortDir
    ]
  );

  const handleExportCsv = useCallback(() => {
    triggerEmployeeRoundsExport(exportParams, true)
      .unwrap()
      .then(data => dispatch(getCSVToExportSuccess(data)))
      .catch(() => {});
  }, [exportParams]);

  const handleClickDownloadPDF = useCallback(() => {
    triggerEmployeeRoundsExport({ ...exportParams, isDownload: true }, true)
      .unwrap()
      .then(data => dispatch(getDataToDownloadSuccess(data)))
      .catch(() => {});
  }, [exportParams]);

  return (
    <ReportContainer id="Employee-Rounds-Report-Container">
      <SearchHeader
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        handleExportCsv={handleExportCsv}
        handleClickDownloadPDF={handleClickDownloadPDF}
      />
      <EmployeeRoundsReportTable
        roundsReportList={listToDisplay}
        loading={
          isGetEmployeeRoundsReportLoading || isGetEmployeeRoundsReportFetching
        }
        totalTableCount={employeeRoundsReportData?.totalRecordCount}
      />
    </ReportContainer>
  );
};

const mapReduxStateToProps = (state: RootState) => ({
  authorizedUser: state.AuthorizedUser.authorizedUser,
  currentEmployeeRoundsReportPage:
    state.ReportReducer.currentEmployeeRoundsReportPage,
  reportSearchQuery: state.ReportReducer.reportSearchQuery,
  roundsReportFilter: state.ReportReducer.roundsReportFilter,
  sortKey: state.ReportReducer.sortRoundsKey,
  sortDir: state.ReportReducer.sortRoundsDir,
  selectedEmpDepartments: state.ConfigReducer.selectedEmpDepartments,
  allEmpDepartments: state.ConfigReducer.empDepartments
});

export default connect(mapReduxStateToProps)(RoundsReport);
